import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import { AuditAction, IAuditListItem } from 'src/types/audit';

type Props = {
  row: IAuditListItem;
};

const formatDiffValue = (value: unknown) => {
  if (value === undefined) {
    return '—';
  }

  if (value === null) {
    return 'null';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

export default function AuditLogTableRow({ row }: Props) {
  const { type, action, actorEmail, before, after, createdAt } = row;

  const collapse = useBoolean();

  const diffKeys = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])
  );

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{type}</TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Label
            variant="soft"
            color={
              (action === AuditAction.CREATED && 'success') ||
              (action === AuditAction.UPDATED && 'info') ||
              (action === AuditAction.DELETED && 'error') ||
              'default'
            }
          >
            {action}
          </Label>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <ListItemText
            primary={actorEmail}
            primaryTypographyProps={{ typography: 'body2' }}
          />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDateTime(createdAt)}</TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton
            color={collapse.value ? 'inherit' : 'default'}
            onClick={collapse.onToggle}
            sx={{
              ...(collapse.value && {
                bgcolor: 'action.hover',
              }),
            }}
          >
            <Iconify icon="eva:arrow-ios-downward-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ p: 0, border: 'none' }} colSpan={5}>
          <Collapse in={collapse.value} timeout="auto" unmountOnExit sx={{ bgcolor: 'background.neutral' }}>
            <Stack component={Paper} sx={{ m: 1.5, p: 2 }}>
              {diffKeys.length === 0 ? (
                <Typography variant="body2" color="text.disabled">
                  No field changes recorded
                </Typography>
              ) : (
                <>
                  <Stack direction="row" sx={{ mb: 1 }}>
                    <Box sx={{ width: '25%' }}>
                      <Typography variant="caption" color="text.disabled">
                        Field
                      </Typography>
                    </Box>
                    <Box sx={{ width: '37.5%' }}>
                      <Typography variant="caption" color="text.disabled">
                        Before
                      </Typography>
                    </Box>
                    <Box sx={{ width: '37.5%' }}>
                      <Typography variant="caption" color="text.disabled">
                        After
                      </Typography>
                    </Box>
                  </Stack>

                  {diffKeys.map((key) => (
                    <Stack
                      key={key}
                      direction="row"
                      alignItems="center"
                      sx={{
                        py: 1,
                        '&:not(:last-of-type)': {
                          borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <Box sx={{ width: '25%' }}>
                        <Typography variant="body2">{key}</Typography>
                      </Box>
                      <Box sx={{ width: '37.5%', wordBreak: 'break-word' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDiffValue(before?.[key])}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '37.5%', wordBreak: 'break-word' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDiffValue(after?.[key])}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </>
              )}
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
